import { useState } from 'react';
import { toast } from './use-toast';

/**
 * Custom hook for managing profile editing state and logic
 * Centralizes all editing handlers for name, bio, and photo
 */
export const useProfileEdit = (user, updateProfile) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameEditText, setNameEditText] = useState(user?.name || '');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioEditText, setBioEditText] = useState(user?.bio || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Name editing handlers
  const handleNameClick = () => {
    setNameEditText(user?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    const trimmedName = nameEditText.trim();
    
    if (!trimmedName) {
      toast({
        title: "Name required",
        description: "Please enter your name",
        variant: "destructive"
      });
      return;
    }

    if (trimmedName.length < 2) {
      toast({
        title: "Name too short",
        description: "Name must be at least 2 characters",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateProfile({ name: trimmedName });
      setIsEditingName(false);
      toast({
        title: "Name updated!",
        description: "Your name has been changed"
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameCancel = () => {
    setNameEditText(user?.name || '');
    setIsEditingName(false);
  };

  // Bio editing handlers
  const handleBioClick = () => {
    setBioEditText(user?.bio || '');
    setIsEditingBio(true);
  };

  const handleBioSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ bio: bioEditText });
      setIsEditingBio(false);
      toast({
        title: "Bio updated!",
        description: "Your bio has been saved"
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBioCancel = () => {
    setBioEditText(user?.bio || '');
    setIsEditingBio(false);
  };

  // Photo handlers
  const handlePhotoClick = () => {
    setShowPhotoDialog(true);
  };

  const handlePhotoDialogClose = () => {
    setShowPhotoDialog(false);
  };

  return {
    // Name editing
    isEditingName,
    nameEditText,
    setNameEditText,
    handleNameClick,
    handleNameSave,
    handleNameCancel,
    
    // Bio editing
    isEditingBio,
    bioEditText,
    setBioEditText,
    handleBioClick,
    handleBioSave,
    handleBioCancel,
    
    // Photo
    showPhotoDialog,
    handlePhotoClick,
    handlePhotoDialogClose,
    isUploadingPhoto,
    setIsUploadingPhoto,
    
    // Shared
    isSaving
  };
};
